import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Save } from 'lucide-react';

export function parseJsonText(text: string): { ok: true; value: unknown } | { ok: false; message: string } {
  if (!text.trim()) {
    return { ok: true, value: null };
  }
  try {
    return { ok: true, value: JSON.parse(text) as unknown };
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : 'Invalid JSON' };
  }
}

interface Work4MeScriptFormProps {
  name: string;
  description: string;
  body: string;
  inputExampleText: string;
  isEnabled: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onInputExampleTextChange: (value: string) => void;
  onIsEnabledChange: (value: boolean) => void;
  onSave: () => void;
  submitLabel?: string;
  submittingLabel?: string;
  editableHint?: string;
}

export function Work4MeScriptForm({
  name,
  description,
  body,
  inputExampleText,
  isEnabled,
  isDirty,
  isSaving,
  onNameChange,
  onDescriptionChange,
  onBodyChange,
  onInputExampleTextChange,
  onIsEnabledChange,
  onSave,
  submitLabel = 'Save Script',
  submittingLabel = 'Saving...',
  editableHint = 'Changes are applied immediately when saved.',
}: Work4MeScriptFormProps) {
  const inputExampleParse = parseJsonText(inputExampleText);
  const canSave = name.trim().length > 0 && body.trim().length > 0 && inputExampleParse.ok && !isSaving;

  return (
    <>
      <div className="grid gap-4 px-4 pt-4">
        <div>
          <Label htmlFor="script-name">Name</Label>
          <Input
            id="script-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="script-description">Description</Label>
          <Textarea
            id="script-description"
            rows={3}
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            className="mt-1 resize-none"
          />
        </div>

        <div>
          <Label htmlFor="script-body">Body</Label>
          <Textarea
            id="script-body"
            rows={16}
            value={body}
            onChange={(event) => onBodyChange(event.target.value)}
            className="mt-1 font-mono text-xs"
            spellCheck={false}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Define function run(input) and return JSON. No network, files or clock; Date.now and Math.random are fixed per run.
          </p>
        </div>

        <div>
          <Label htmlFor="script-input-example">Input example</Label>
          <Textarea
            id="script-input-example"
            rows={6}
            value={inputExampleText}
            onChange={(event) => onInputExampleTextChange(event.target.value)}
            className="mt-1 font-mono text-xs"
            spellCheck={false}
          />
          <p className={`mt-1 text-xs ${inputExampleParse.ok ? 'text-muted-foreground' : 'text-destructive'}`}>
            {inputExampleParse.ok ? 'JSON passed to run(input) by default.' : `Invalid JSON: ${inputExampleParse.message}`}
          </p>
        </div>

        <div className="flex items-center justify-between py-1">
          <div>
            <p className="text-sm font-medium">Enabled</p>
            <p className="text-xs text-muted-foreground">Disabled scripts stay stored but refuse to run.</p>
          </div>
          <Switch checked={isEnabled} onCheckedChange={onIsEnabledChange} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
        <p className="text-xs text-muted-foreground">{editableHint}</p>
        <Button onClick={onSave} className="gap-2" disabled={!canSave || !isDirty}>
          <Save className="h-4 w-4" />
          {isSaving ? submittingLabel : submitLabel}
        </Button>
      </div>
    </>
  );
}
